import dayjs from 'dayjs';

const RELATIVE_SINGLE = {
    'today': (now) => now.startOf('day'),
    'yesterday': (now) => now.subtract(1, 'day').startOf('day'),
    'tomorrow': (now) => now.add(1, 'day').startOf('day'),
    'start-of-week': (now) => now.startOf('week'),
    'end-of-week': (now) => now.endOf('week'),
    'start-of-month': (now) => now.startOf('month'),
    'end-of-month': (now) => now.endOf('month'),
    'start-of-year': (now) => now.startOf('year'),
    'end-of-year': (now) => now.endOf('year'),
};

/**
 * Resolve a value into a Date object.
 * Supports Date, timestamp, ISO string, and relative keywords (today, start-of-month, etc.).
 */
export const resolveDateInput = (value, now = dayjs()) => {
    if (!value && value !== 0) return null;
    if (value instanceof Date && !isNaN(value)) return value;
    if (typeof value === 'number') {
        const d = new Date(value);
        return isNaN(d) ? null : d;
    }
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        const resolver = RELATIVE_SINGLE[normalized];
        if (resolver) {
            const d = resolver(now);
            return d?.isValid() ? d.toDate() : null;
        }
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.toDate() : null;
    }
    return null;
};

/**
 * Resolve a preset range definition into a [start, end] tuple of Dates.
 * Accepts strings (today, this-week, this-month, this-year),
 * tuples like ['start-of-year', 'today'], or objects { from, to }.
 */
export const resolveRangePreset = (preset, now = dayjs()) => {
    const resolveSingle = (val) => resolveDateInput(val, now);

    const normalizeTuple = (from, to) => {
        const start = resolveSingle(from);
        const end = resolveSingle(to);
        if (!start && !end) return [null, null];
        if (start && end) return [start, end];
        return start ? [start, start] : [end, end];
    };

    if (Array.isArray(preset)) {
        return normalizeTuple(preset[0], preset[1]);
    }

    if (typeof preset === 'object' && preset !== null) {
        if (preset.from || preset.to) {
            return normalizeTuple(preset.from, preset.to);
        }
    }

    if (typeof preset === 'string') {
        const normalized = preset.trim().toLowerCase();
        if (normalized === 'today') {
            const start = now.startOf('day');
            const end = now.endOf('day');
            return [start.toDate(), end.toDate()];
        }
        if (normalized === 'this-week') {
            const start = now.startOf('week');
            const end = now.endOf('week');
            return [start.toDate(), end.toDate()];
        }
        if (normalized === 'this-month') {
            const start = now.startOf('month');
            const end = now.endOf('month');
            return [start.toDate(), end.toDate()];
        }
        if (normalized === 'this-year') {
            const start = now.startOf('year');
            const end = now.endOf('year');
            return [start.toDate(), end.toDate()];
        }
        if (normalized === 'this-quarter') {
            // Quarter support without plugin: derive from month index
            const month = now.month();
            const quarterStartMonth = Math.floor(month / 3) * 3;
            const start = now.month(quarterStartMonth).startOf('month');
            const end = start.add(2, 'month').endOf('month');
            return [start.toDate(), end.toDate()];
        }
    }

    return [null, null];
};

/**
 * Clamp a range to min/max bounds while keeping ordering.
 */
export const clampRangeToBounds = (range, minDate, maxDate) => {
    if (!Array.isArray(range)) return [null, null];
    const clamp = (d) => {
        if (!d) return null;
        let out = d;
        if (minDate && dayjs(out).isBefore(minDate, 'day')) out = minDate;
        if (maxDate && dayjs(out).isAfter(maxDate, 'day')) out = maxDate;
        return out;
    };
    const start = clamp(range[0]);
    const end = clamp(range[1]);
    if (start && end && dayjs(start).isAfter(end)) return [end, end];
    return [start, end];
};

const parseMaxRange = (maxRange) => {
    if (!maxRange) return null;
    if (typeof maxRange === 'number') return { value: maxRange, unit: 'day' };
    if (typeof maxRange === 'string') {
        const match = maxRange.trim().toLowerCase().match(/^(\d+)\s*-(day|week|month|year)s?$/);
        if (match) return { value: Number(match[1]), unit: match[2] };
    }
    if (typeof maxRange === 'object') {
        const value = Number(maxRange.value ?? maxRange.count ?? maxRange.amount ?? maxRange.days);
        const unit = maxRange.unit || (maxRange.month ? 'month' : maxRange.year ? 'year' : 'day');
        if (Number.isFinite(value) && value > 0) return { value, unit };
    }
    return null;
};

/**
 * Enforce an upper bound on range length (e.g., max 1 month).
 */
export const enforceMaxRange = (range, maxRange) => {
    if (!Array.isArray(range) || !range[0] || !range[1]) return range;
    const parsed = parseMaxRange(maxRange);
    if (!parsed) return range;
    const { value, unit } = parsed;
    const start = dayjs(range[0]);
    const end = dayjs(range[1]);
    if (!start.isValid() || !end.isValid()) return range;
    const allowedEnd = start.add(value, unit).endOf('day');
    if (end.isAfter(allowedEnd)) {
        return [start.toDate(), allowedEnd.toDate()];
    }
    return range;
};



